import { Icon } from '@iconify/react'
import { ScrollShadow } from '@nextui-org/scroll-shadow'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import { isEmpty } from 'radash'
import { FC, Fragment } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import {
  IconButton,
  Sheet,
  SheetContent, SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~in-app/components'
import { NativeMenuRecursiveRender } from '~in-app/components/layout/console/native-sidebar/NativeMenuRecursiveRender'
import { formatUrlPathname } from '~in-app/lib/utils'
import { routerList } from '~in-app/routerList'

import { NativeMenuSection } from './NativeMenuSection'

const animationVariants: Variants = {
  init: {},
  maximize: {},
  minimize: {},
  exit: {},
}

const buttonAnimationVariants: Variants = {
  init: {
    opacity: 0,
  },
  show: {
    opacity: 1,
    transition: {
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      ease: 'easeInOut',
    },
  },
}

const NativeSidebar: FC = () => {
  const show = useMediaQuery('(max-width:640px)')

  return (
    <Sheet>
      <SheetTrigger asChild>
        <span>
          <AnimatePresence>
            {
              show
                ? (
                    <motion.div
                      key="ConsoleNativeSidebarTriggerButton"
                      animate="show"
                      exit="exit"
                      initial="init"
                      variants={buttonAnimationVariants}
                    >
                      <IconButton color="muted">
                        <Icon icon="lucide:menu" />
                      </IconButton>
                    </motion.div>
                  )
                : (
                    <motion.span
                      key="ConsoleNativeSidebarEmpty"
                      className="hidden"
                    />
                  )
            }
          </AnimatePresence>
        </span>

      </SheetTrigger>

      <SheetContent side="left">
        <SheetHeader>
          <SheetTitle className="text-center text-3xl">
            PLM
          </SheetTitle>

          <SheetDescription />
        </SheetHeader>

        <ScrollShadow
          hideScrollBar
          className="h-full"
        >
          <nav className="mt-8">
            <motion.ul
              animate="maximize"
              className="flex flex-col gap-2 px-2"
              exit="exit"
              initial="init"
              variants={animationVariants}
            >
              {
                (routerList['console']?.items || []).map(({ sectionLabel, children, ...props }) => {
                  const hasSectionLabel = !isEmpty(sectionLabel)
                  const rootPath = routerList['console'].rootPath
                  const path = formatUrlPathname(rootPath + (props.path || ''))

                  return (
                    <Fragment key={`NativeSidebarMenus_${props.label}_${props.path}`}>
                      {
                        hasSectionLabel && (
                          <NativeMenuSection
                            key={`NativeMenuSection_${sectionLabel}_${path}`}
                            sectionLabel={sectionLabel}
                          />
                        )
                      }

                      <NativeMenuRecursiveRender
                        key={`NativeMenuRecursiveRender${props.label}_${path}`}
                        icon={props.icon}
                        isSection={hasSectionLabel}
                        items={children}
                        label={props.label}
                        path={path}
                      />
                    </Fragment>
                  )
                })
              }
            </motion.ul>
          </nav>
        </ScrollShadow>
      </SheetContent>
    </Sheet>
  )
}
NativeSidebar.displayName = 'ConsoleNativeSidebar'

export { NativeSidebar }
